import mapPictures from "../../../../shared/image-mapper";
import OnboardingType from "../../../../shared/onboarding/onboarding-type";
import { sortByName } from "./../../../../shared/utility";
import { CHANGE_ANIMAL_TO_PROP, ANIMAL_SCENE_SETTING, ANIMAL_SET_RETURN_URL } from "./actions";

interface ActionState {
    animals: any[];
    loading: boolean;
    loadingSceneSettings: boolean;
    loadingScenes: boolean;
    animal: any;
    imageFiles: any[];
    scenes: any[];
    readOnly: boolean;
    onboardingSteps: OnboardingType[];
    findingInScript: boolean;
    searchingScenes: boolean;
    enableSave: boolean;
    returnUrl: string | null;
}

export const OnboardingStep1ClassName = 'animal-scenes-onboarding-step-1';
export const OnboardingStep2ClassName = 'animal-scenes-onboarding-step-2';
export const OnboardingStep3ClassName = 'animal-scenes-onboarding-step-3';
export const OnboardingStep4ClassName = 'animal-scenes-onboarding-step-4';
export const OnboardingStep5ClassName = 'animal-scenes-onboarding-step-5';

const defaultState: ActionState = {
    animals: [],
    loading: true,
    loadingSceneSettings: false,
    loadingScenes: true,
    animal: {},
    imageFiles: [],
    scenes: [],
    readOnly: false,
    findingInScript: false,
    searchingScenes: false,
    enableSave: false,
    returnUrl: null,
    onboardingSteps: [
        {
            target: '.' + OnboardingStep1ClassName,
            title: 'Scene Search',
            content: `        
        Use this to quickly select the scenes that match the selected criteria.
      `
        },
        {
            target: '.' + OnboardingStep2ClassName,
            title: 'Script Search',
            content: `Or use this to search the script for the name of the animal, and select any scenes that match. You can chose which parts of the script to select.`
        },
        {
            target: '.' + OnboardingStep3ClassName,
            title: 'Select',
            content: `Or just select or unselect all scenes.`
        },
        {
            target: '.' + OnboardingStep4ClassName,
            title: 'Manual',
            content: `<p>If any scenes have not been selected using any of the above methods, then you can manually select or unselect a scene.</p>
                <p>This list only includes scenes which are set at the selected scene setting.</p>`
        },
        {
            target: '.' + OnboardingStep5ClassName,
            title: 'Save',
            content: `Once you have the desired scenes selected, don't forget to click Save.`
        }
    ]
};

const reducer = (state = defaultState, action: any = {}) => {
    switch (action.type) {
        case "FETCH_ANIMALS_PENDING": {
            return {
                ...state,
                loading: true,
                errors: null
            };
        }

        case "CLEAR_ANIMAL": {
            return {
                ...state,
                loading: false,
                animal: null,
                errors: null
            };
        }

        case "ANIMAL_SET_NAME_AND_SCENE_SETTING": {
            const animal = { name: action.meta.name, sceneSettingId: action.meta.sceneSettingId, quantity: 1, nameInScript: action.meta.name, acquisitionMethod: 1, enableSave: true }
            return {
                ...state,
                animal: animal
            };
        }

        case ANIMAL_SCENE_SETTING: {
            const animal = { ...state.animal, sceneSettingId: action.meta.sceneSettingId, enableSave: true }
            return {
                ...state,
                animal: animal
            };
        }

        case ANIMAL_SET_RETURN_URL: {
            return {
                ...state,
                returnUrl: action.meta.returnUrl
            };
        }

        case "FETCH_ANIMALS_FULFILLED": {
            return {
                ...state,
                loading: false,
                animals: action.payload.data
            };
        }

        case "FETCH_ANIMALS_REJECTED": {
            return {
                ...state,
                loading: false,
                errors: action.payload.response.data
            };
        }

        case "FETCH_SINGLE_ANIMAL_PENDING": {
            return {
                ...state,
                loading: true,
                animal: null,
                errors: null
            };
        }

        case "FETCH_SINGLE_ANIMAL_FULFILLED": {
            const animal = action.payload.data;
            mapPictures(animal);
            return {
                ...state,
                loading: false,
                animal: animal,
                readOnly: animal.readOnly
            };
        }

        case "FETCH_SINGLE_ANIMAL_REJECTED": {
            return {
                ...state,
                loading: false,
                errors: action.payload.response.data
            };
        }

        case "ADD_SINGLE_ANIMAL_PENDING": {
            return {
                ...state,
                loading: true,
                errors: null
            };
        }

        case "ADD_SINGLE_ANIMAL_FULFILLED": {
            const animal = action.payload.data;
            return {
                ...state,
                loading: false,
                redirect: { to: "scenes", id: animal.id }
            };
        }

        case "ADD_SINGLE_ANIMAL_REJECTED": {
            return {
                ...state,
                loading: false,
                errors: action.payload.response.data
            };
        }

        case "UPDATE_SINGLE_ANIMAL_PENDING": {
            return {
                ...state,
                loading: true,
                errors: null
            };
        }

        case "UPDATE_SINGLE_ANIMAL_FULFILLED": {
            const animal = action.meta.animal;
            return {
                ...state,
                loading: false,
                redirect: { to: "list" }
            };
        }

        case "UPDATE_SINGLE_ANIMAL_REJECTED": {
            return {
                ...state,
                loading: false,
                errors: action.payload.response.data
            };
        }

        case "DELETE_SINGLE_ANIMAL_PENDING": {
            return {
                ...state,
                loading: true,
                errors: null
            };
        }

        case "DELETE_SINGLE_ANIMAL_FULFILLED": {
            const animal = action.meta.animal;
            const animals = state.animals.filter((p) => p.id !== animal.id);
            return {
                ...state,
                loading: false,
                animals: animals
            };
        }

        case "DELETE_SINGLE_ANIMAL_REJECTED": {
            return {
                ...state,
                loading: false,
                errors: action.payload.response.data
            };
        }

        case "ADD_ANIMAL_IMAGE_FILES": {
            const imageFiles = action.meta.imageFiles;
            const animal = { ...state.animal, pictures: imageFiles };
            return {
                ...state,
                animal: animal
            };
        }

        case "ADD_ANIMAL_IMAGE_URL": {
            const animal = { ...state.animal };
            if (!animal.imageUrls) {
                animal.imageUrls = [];
            }
            animal.imageUrls.push(action.meta.url);
            return {
                ...state,
                animal: animal
            };
        }

        case "DELETE_ANIMAL_IMAGE_URL": {
            const animal = { ...state.animal };
            animal.imageUrls = animal.imageUrls.filter(
                (url) => url !== action.meta.url
            );
            return {
                ...state,
                animal: animal
            };
        }

        case "DELETE_ANIMAL_IMAGE_FILE": {
            const animal = { ...state.animal };
            animal.pictures = animal.pictures.filter(
                (file) => file.name !== action.meta.fileName
            );
            return {
                ...state,
                animal: animal
            };
        }

        case "ANIMAL_MAIN_IMAGE_CHANGED": {
            const animal = { ...state.animal, mainImageUrl: action.meta.url };
            return {
                ...state,
                animal: animal
            };
        }

        case "CLEAR_REDIRECT": {
            return {
                ...state,
                redirect: null
            };
        }

        case "FETCH_SCENES_ASSIGNED_TO_ANIMAL_PENDING": {
            return {
                ...state,
                loadingScenes: true
            };
        }

        case "FETCH_SCENES_ASSIGNED_TO_ANIMAL_FULFILLED": {
            const scenes = action.payload.data;
            return {
                ...state,
                loadingScenes: false,
                scenes: scenes,
                enableSave: false
            };
        }

        case "FETCH_SCENES_ASSIGNED_TO_ANIMAL_REJECTED": {
            return {
                ...state,
                loadingScenes: false
            };
        }

        case "SET_SCENE_ASSIGNED_TO_ANIMAL": {
            const scenes = [...state.scenes];
            const scene = scenes.find(s => s.id === action.meta.sceneId);
            if (scene) {
                scene.assigned = !scene.assigned;
            }

            return {
                ...state,
                scenes: scenes,
                enableSave: true
            };
        }

        case "ASSIGN_SCENES_TO_ANIMAL_PENDING": {
            return {
                ...state,
                loading: true
            };
        }

        case "ASSIGN_SCENES_TO_ANIMAL_FULFILLED": {
            return {
                ...state,
                loading: false,
                redirect: { to: "list" }
            };
        }

        case "ASSIGN_SCENES_TO_ANIMAL_REJECTED": {
            return {
                ...state,
                loading: false
            };
        }

        case "FETCH_ALL_SCENE_SETTINGS_FOR_ANIMAL_PENDING": {
            return {
                ...state,
                loadingSceneSettings: true
            };
        }

        case "FETCH_ALL_SCENE_SETTINGS_FOR_ANIMAL_FULFILLED": {
            const sceneSettings = action.payload.data;
            sortByName(sceneSettings);
            return {
                ...state,
                allSceneSettings: sceneSettings,
                loadingSceneSettings: false
            };
        }



        case "FETCH_ALL_SCENE_SETTINGS_FOR_ANIMAL_REJECTED": {
            return {
                ...state,
                loadingSceneSettings: false
            };
        }

        case "FETCH_ALL_SCENES_FOR_ANIMAL_PENDING": {
            return {
                ...state,
                searchingScenes: true
            };
        }

        case "FETCH_ALL_SCENES_FOR_ANIMAL_FULFILLED": {
            const scenes = [...state.scenes];
            const searchedScenes = action.payload.data;
            searchedScenes.forEach(searchedScene => {
                const scene = scenes.find(s => s.id === searchedScene.id);
                if (scene) {
                    scene.assigned = true;
                }
            });

            return {
                ...state,
                scenes: scenes,
                searchingScenes: false,
                enableSave: true
            };
        }

        case "FETCH_ALL_SCENES_FOR_ANIMAL_REJECTED": {
            return {
                ...state,
                searchingScenes: false
            };
        }

        case "SEARCH_SCRIPT_FOR_ANIMAL_PENDING": {
            return {
                ...state,
                findingInScript: true
            };
        }

        case "SEARCH_SCRIPT_FOR_ANIMAL_FULFILLED": {
            const scenes = [...state.scenes];
            const scriptScenes = action.payload.data.scenes;
            scriptScenes.forEach(scriptScene => {
                const scene = scenes.find(s => s.id === scriptScene.id);
                if (scene) {
                    scene.assigned = true;
                }
            });

            return {
                ...state,
                scenes: scenes,
                findingInScript: false,
                enableSave: true
            };
        }

        case "SEARCH_SCRIPT_FOR_ANIMAL_REJECTED": {
            return {
                ...state,
                findingInScript: false
            };
        }

        case "CLEAR_SEARCH_SCENES_FOR_ANIMAL": {
            const scenes = [...state.scenes];
            scenes.forEach(s => s.assigned = false);
            return {
                ...state,
                scenes: scenes,
                enableSave: true
            };
        }

        case CHANGE_ANIMAL_TO_PROP + "_FULFILLED": {
            const animal = action.meta.animal;
            const animals = state.animals.filter((p) => p.id !== animal.id);
            return {
                ...state,
                loading: false,
                animals: animals,
                redirect: { to: "list" }
            };
        }

        default:
            return state;
    }
};

export default reducer;
